import * as enabledApi from '@shared/api/enabledPlayApi';
import { getCustomControlModels } from '@shared/constants/controls/CustomControlUtils';
import { createUUID, CUSTOM_CONTROLS_STORAGE } from '@shared/constants/Keys';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import MacroModel from '@shared/models/deviceProfiles/MacroModel';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import { CustomControlModel } from '@shared/models/devices/CustomControlModel';
import { CustomControlsModel } from '@shared/models/devices/CustomControlsModel';
import UserDeviceCustomControlOverrideModel from '@shared/models/devices/UserDeviceCustomControlOverrideModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import { UserAppInterfaceControlOverride } from '@shared/models/users/UserAppInterfaceControlOverride';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { publish, subscribe } from '../helpers/EventCallback';
import { useCurrentDeviceStore } from './CurrentDeviceContainer';
import { useDeviceStore } from './DeviceContainer';

interface CurrentDeviceControls {
    global: CustomControlModel[] | undefined;
    device: CustomControlModel[] | undefined;
}

interface GlobalCustomControlsState {
    // State
    availableCustomControls: AvailableCustomControlsModel[] | null;
    currentDeviceControls: CurrentDeviceControls;
    customControlsLocale: string;
    errors: string[];
    isLoading: boolean;
    profileCustomControls: AvailableCustomControlsModel[] | null;
    resetDeviceControls: boolean;
    userLocale: string;
    voiceDictationCustomControls: Set<string>;
    voicePausedCustomControls: Set<string>;
    voicePrefixCustomControls: Set<string>;

    // Actions
    addDeviceControls: (device: UserDeviceModel) => Promise<SuccessResult<boolean> | UnexpectedResult<boolean>>;
    clear: () => void;
    initializeFromRemote: (
        localePreferences: UpdateUserLocaleRequest,
        virtualDeviceId: string | null
    ) => Promise<SuccessResult<boolean> | UnexpectedResult<boolean>>;
    getAllCustomCommandsMap: () => Map<
        string,
        { id: string; source: string; value: string; name: string; deviceName?: string }
    >;
    getDeviceControlName: (controlId: string) => string;
    getDeviceCustomControls: (userDeviceId: string) => Promise<any>;
    getFinalControlMacro: (
        command: string,
        currentDeviceId: string,
        additionalInputContent?: string,
        isEligibleControl?: (control: CustomControlModel) => boolean
    ) => MacroModel | null;
    getFinalDictationControlMacro: (
        command: string,
        currentDeviceId: string,
        isPaused: boolean,
        additionalInputContent?: string
    ) => MacroModel | null;
    getPartialControlMacro: (
        command: string,
        currentDeviceId: string,
        additionalInputContent?: string,
        isEligibleControl?: (control: CustomControlModel) => boolean
    ) => MacroModel | null;
    initializeFromStorage: (localePreferences: UpdateUserLocaleRequest) => void;
    removeDeviceControls: (deviceId: string) => void;
    resetCustomControls: (localePreferences: UpdateUserLocaleRequest) => Promise<void>;
    resetVoiceCustomControls: () => void;
    setAvailableCustomControls: (controls: AvailableCustomControlsModel[] | null) => void;
    setResetDeviceControls: (v: boolean) => void;
    snapshotState: () => void;
    updateAppInterfaceControls: (overrides: UserAppInterfaceControlOverride[]) => Promise<any>;
    updateCustomUserDeviceControls: (deviceId: string, controls: CustomControlModel[], locale: string) => Promise<any>;
    updateDeviceCustomControls: (deviceId: string, overrides: UserDeviceCustomControlOverrideModel[]) => Promise<any>;
    updateLocalAppInterfaceControls: (controls: CustomControlsModel) => void;
    updateLocalDeviceCustomControls: (controls: CustomControlsModel) => void;
}

const computeCurrentDeviceControls = (
    available: AvailableCustomControlsModel[] | null,
    currentDeviceId: string | undefined
): CurrentDeviceControls => {
    const global = available?.find((d) => d.deviceId === 'global')?.controls;
    const device = available?.find((d) => d.deviceId === currentDeviceId)?.controls;
    return { global, device };
};

export const useGlobalCustomControlsStore = create<GlobalCustomControlsState>()(
    immer((set, get) => {
        const handleVoiceDictationControlsUpdate = (dictationControls: Set<string>) => {
            set((state) => {
                state.voiceDictationCustomControls = dictationControls;
            });
            publish('update-voice-dictation-controls', dictationControls);
        };

        const snapshotState = () => {
            const state = get();
            localStorage.setItem(
                CUSTOM_CONTROLS_STORAGE,
                JSON.stringify({
                    available: state.availableCustomControls,
                    profile: state.profileCustomControls,
                    paused: [...state.voicePausedCustomControls],
                    dictation: [...state.voiceDictationCustomControls],
                    prefix: [...state.voicePrefixCustomControls],
                })
            );
        };

        const resetVoiceCustomControls = () => {
            const paused: string[] = [];
            const dictation: string[] = [];
            const prefix: string[] = [];

            get().availableCustomControls?.forEach((deviceControls) => {
                if (deviceControls.deviceId !== deviceControls.deviceTypeId || deviceControls.deviceId === 'global') {
                    deviceControls.controls
                        .filter((c) => c.allowWhenVoicePaused)
                        .forEach((c) => {
                            if (c.commands) paused.push(...c.commands);
                        });
                    deviceControls.controls
                        .filter((c) => c.allowWhenDictating)
                        .forEach((c) => {
                            if (c.commands) dictation.push(...c.commands);
                        });
                    deviceControls.controls
                        .filter((c) => c.isPrefixVoiceCommand)
                        .forEach((c) => {
                            if (c.commands) prefix.push(...c.commands);
                        });
                }
            });

            set((state) => {
                state.voicePausedCustomControls = new Set(paused);
                state.voicePrefixCustomControls = new Set(prefix);
            });
            handleVoiceDictationControlsUpdate(new Set(dictation));
        };

        const setAvailableControlsState = (
            available: AvailableCustomControlsModel[] | null,
            profile?: AvailableCustomControlsModel[] | null
        ) => {
            const currentDeviceId = useCurrentDeviceStore.getState().id;
            const deviceControls = computeCurrentDeviceControls(available, currentDeviceId);
            set((state) => {
                state.availableCustomControls = available;
                state.currentDeviceControls = deviceControls;
                if (profile !== undefined) {
                    state.profileCustomControls = profile;
                }
            });
        };

        const updateAvailableCustomControls = (
            index: number,
            controls: CustomControlModel[],
            deviceId: string,
            deviceTypeId: string,
            name: string
        ) => {
            const current = get().availableCustomControls;
            if (current) {
                let updated: AvailableCustomControlsModel[];
                if (index < 0) {
                    updated = [...current, { deviceId, deviceTypeId, name, controls }];
                } else {
                    updated = current.map((d, i) => (i === index ? { ...d, controls } : d));
                }
                setAvailableControlsState(updated);
            }
            resetVoiceCustomControls();
            snapshotState();
        };

        const cleanCommand = (command: string): string => command.toLowerCase().trim();

        const separatePrefixCommand = (command: string): { prefix: string; phrase: string } | undefined => {
            const voicePrefixCustomControls = get().voicePrefixCustomControls;
            if (!voicePrefixCustomControls || !command) return undefined;
            for (const prefix of voicePrefixCustomControls) {
                if (command.toLowerCase().startsWith(prefix)) {
                    let idx = prefix.length;
                    while (command[idx] === ' ') idx++;
                    return { prefix, phrase: command.substring(idx) };
                }
            }
            return undefined;
        };

        const findCustomControl = (
            command: string,
            currentDeviceId: string,
            isEligibleControl: (control: CustomControlModel) => boolean,
            additionalInputContent?: string
        ): MacroModel | null => {
            const availableCustomControls = get().availableCustomControls;
            if (!availableCustomControls) return null;
            for (const deviceControls of availableCustomControls) {
                if (deviceControls.deviceId === currentDeviceId || deviceControls.deviceId === 'global') {
                    for (const control of deviceControls.controls) {
                        if (isEligibleControl(control) && control.commands?.includes(command)) {
                            return convertCustomControlToMacro(deviceControls, control, additionalInputContent);
                        }
                    }
                }
            }
            return null;
        };

        const convertCustomControlToMacro = (
            customControls: AvailableCustomControlsModel,
            control: CustomControlModel,
            additionalInputContent?: string
        ): MacroModel => ({
            id: control.id,
            name: control.name,
            commands: control.commands || [],
            events: [
                {
                    eventType: 'DeviceTypeCustomAction',
                    additionalInputContent,
                    deviceTypeCustomActionId: control.id,
                    deviceTypeId: customControls.deviceId,
                },
            ],
        });

        const setResetDeviceControls = (v: boolean) =>
            set((state) => {
                state.resetDeviceControls = v;
            });

        const setAvailableCustomControls = (controls: AvailableCustomControlsModel[] | null) =>
            setAvailableControlsState(controls);

        const initializeFromStorage = (localePreferences: UpdateUserLocaleRequest) => {
            try {
                set((state) => {
                    state.isLoading = true;
                });
                const storedControls = localStorage.getItem(CUSTOM_CONTROLS_STORAGE);
                if (storedControls) {
                    const parsedControls = JSON.parse(storedControls);
                    const devices = useDeviceStore.getState().devices;
                    const available: AvailableCustomControlsModel[] = [];

                    if (devices) {
                        for (const device of devices) {
                            const storedDeviceControls = parsedControls.available.find(
                                (d: AvailableCustomControlsModel) => d.deviceId === device.id
                            );
                            if (storedDeviceControls) {
                                available.push({
                                    deviceId: device.id,
                                    deviceTypeId: device.device?.deviceType?.id || device.id,
                                    name: useDeviceStore.getState().getDeviceName(device.id),
                                    controls: storedDeviceControls.controls,
                                });
                            }
                        }
                    }

                    const globalControls = parsedControls.available.find(
                        (d: AvailableCustomControlsModel) => d.deviceId === 'global'
                    );
                    if (globalControls) {
                        available.push({
                            deviceId: 'global',
                            deviceTypeId: 'global',
                            name: 'Cephable',
                            controls: globalControls.controls,
                        });
                    }

                    setAvailableControlsState(available, parsedControls.profile);
                    set((state) => {
                        state.customControlsLocale = localePreferences.defaultVoiceLocale;
                        state.voicePausedCustomControls = new Set(parsedControls.paused);
                        state.voicePrefixCustomControls = new Set(parsedControls.prefix);
                    });
                    handleVoiceDictationControlsUpdate(new Set(parsedControls.dictation));
                }
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Failed to parse custom controls from local storage.'];
                });
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        const initializeFromRemote = async (
            localePreferences: UpdateUserLocaleRequest,
            virtualDeviceId: string | null
        ) => {
            try {
                set((state) => {
                    state.isLoading = true;
                    state.userLocale = localePreferences.defaultVoiceLocale;
                    state.customControlsLocale = localePreferences.defaultVoiceLocale;
                });
                const locale = localePreferences.defaultVoiceLocale;
                const interfaceResult = await enabledApi.getUserAppInterfaceControls(locale);
                const virtualDeviceResult = virtualDeviceId
                    ? await enabledApi.getDeviceCustomControls(virtualDeviceId, locale)
                    : new UnexpectedResult<CustomControlsModel>();

                const available: AvailableCustomControlsModel[] = [];
                const profile: AvailableCustomControlsModel[] = [];
                const devices = useDeviceStore.getState().devices;

                if (devices) {
                    for (const device of devices) {
                        if (device.isVerified === false) continue;
                        const deviceType = device.device?.deviceType;
                        if (
                            deviceType &&
                            device.customControls.length === 0 &&
                            device.customDeviceTypeControlOverrides.length === 0
                        ) {
                            const dtResult = await enabledApi.getDeviceTypeCustomControls(deviceType.id, locale);
                            if (dtResult?.resultType === 'Ok' && dtResult.data) {
                                device.customControls = dtResult.data.controls.map(
                                    (c) => ({ ...c, isDeviceTypeControl: true }) as CustomControlModel
                                );
                            }
                        }
                        if (
                            device.customControls.length === 0 &&
                            device.customDeviceTypeControlOverrides.length === 0
                        ) {
                            continue;
                        }
                        const deviceId = device.id;
                        const deviceName = useDeviceStore.getState().getDeviceName(deviceId);
                        const isPrivate = deviceType?.isPrivate;

                        let controls: CustomControlModel[];
                        if (
                            deviceId === virtualDeviceId &&
                            virtualDeviceResult?.resultType === 'Ok' &&
                            virtualDeviceResult.data
                        ) {
                            controls = virtualDeviceResult.data.controls;
                        } else {
                            controls = getCustomControlModels(
                                device.customControls,
                                device.customDeviceTypeControlOverrides
                            );
                        }

                        available.push({
                            deviceId,
                            deviceTypeId: deviceType.id,
                            name: deviceName,
                            isPrivate,
                            controls,
                        });
                        const profileControls = controls.filter((c) => c.isProfileControl);
                        if (profileControls.length > 0) {
                            profile.push({
                                deviceId,
                                deviceTypeId: deviceType.id,
                                name: deviceName,
                                isPrivate,
                                controls: profileControls,
                            });
                        }
                    }
                }

                if (interfaceResult?.resultType === 'Ok' && interfaceResult.data) {
                    const globalDevice: AvailableCustomControlsModel = {
                        deviceId: 'global',
                        deviceTypeId: 'global',
                        name: 'Cephable',
                        isPrivate: false,
                        controls: interfaceResult.data.controls,
                    };
                    available.push(globalDevice);
                    const profileInterface = interfaceResult.data.controls.filter((c) => c.isProfileControl);
                    if (profileInterface.length > 0) profile.push(globalDevice);
                }

                setAvailableControlsState(available, profile);
                resetVoiceCustomControls();
                snapshotState();
                return new SuccessResult<boolean>(true);
            } catch (error) {
                console.log(error);
                set((state) => {
                    state.errors = [String(error)];
                });
                return new UnexpectedResult<boolean>();
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        const getDeviceCustomControls = async (userDeviceId: string) => {
            try {
                const locale = get().customControlsLocale;
                return await enabledApi.getDeviceCustomControls(userDeviceId, locale);
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error getting device custom controls'];
                });
                return new UnexpectedResult<CustomControlsModel>();
            }
        };

        const getDeviceControlName = (controlId: string) => {
            const availableCustomControls = get().availableCustomControls;
            if (!availableCustomControls) return controlId;
            for (const deviceCtrl of availableCustomControls) {
                for (const control of deviceCtrl.controls) {
                    if (control.id === controlId) return control.name;
                }
            }
            return controlId;
        };

        const getFinalControlMacro = (
            command: string,
            currentDeviceId: string,
            additionalInputContent: string,
            isEligibleControl?: (control: CustomControlModel) => boolean
        ) => {
            const cleanedCommand = cleanCommand(command);
            if (cleanedCommand.length === 0) return null;
            const prefixResult = separatePrefixCommand(command);
            let effectiveCommand = cleanedCommand;
            let effectiveInput = additionalInputContent;
            if (prefixResult) {
                effectiveCommand = prefixResult.prefix;
                effectiveInput = prefixResult.phrase;
            }
            const isEligible = isEligibleControl ?? (() => true);
            return findCustomControl(effectiveCommand, currentDeviceId, isEligible, effectiveInput);
        };

        const getFinalDictationControlMacro = (
            command: string,
            currentDeviceId: string,
            isPaused: boolean,
            additionalInputContent?: string
        ) => {
            const cleanedCommand = cleanCommand(command);
            if (cleanedCommand.length === 0) return null;
            const prefixResult = separatePrefixCommand(command);
            let effectiveCommand = cleanedCommand;
            let effectiveInput = additionalInputContent;
            if (prefixResult) {
                effectiveCommand = prefixResult.prefix;
                effectiveInput = prefixResult.phrase;
            }
            const state = get();
            if (isPaused) {
                if (!state.voicePausedCustomControls.has(effectiveCommand)) return null;
            } else {
                if (!state.voiceDictationCustomControls.has(effectiveCommand)) return null;
            }
            return findCustomControl(effectiveCommand, currentDeviceId, () => true, effectiveInput);
        };

        const getPartialControlMacro = (
            command: string,
            currentDeviceId: string,
            additionalInputContent?: string,
            isEligibleControl?: (control: CustomControlModel) => boolean
        ) => {
            const cleanedCommand = cleanCommand(command);
            if (cleanedCommand.length === 0) return null;
            const isEligible = isEligibleControl ?? (() => true);
            return findCustomControl(
                cleanedCommand,
                currentDeviceId,
                (control) => !control.isPrefixVoiceCommand && !control.allowWhenDictating && isEligible(control),
                additionalInputContent
            );
        };

        const getAllCustomCommandsMap = () => {
            const map = new Map<
                string,
                { id: string; source: string; value: string; name: string; deviceName?: string }
            >();
            const availableCustomControls = get().availableCustomControls;
            if (!availableCustomControls) return map;
            for (const deviceControls of availableCustomControls) {
                if (deviceControls.controls) {
                    for (const control of deviceControls.controls) {
                        if (control.commands) {
                            for (const command of control.commands) {
                                const key = deviceControls.deviceId + createUUID();
                                map.set(key, {
                                    id: control.id,
                                    source: deviceControls.deviceId,
                                    value: command.toLowerCase(),
                                    name: control.name,
                                    deviceName: deviceControls.name,
                                });
                            }
                        }
                    }
                }
            }
            return map;
        };

        const updateAppInterfaceControls = async (overrides: UserAppInterfaceControlOverride[]) => {
            const availableCustomControls = get().availableCustomControls;
            const allControls = availableCustomControls?.find((d) => d.deviceId === 'global')?.controls || [];
            const sendControls: UserAppInterfaceControlOverride[] = allControls.map((c) => {
                const override = overrides.find((o) => o.appInterfaceControlId === c.id);
                return { appInterfaceControlId: c.id, commands: override?.commands || c?.commands || [] };
            });
            try {
                const locale = get().customControlsLocale;
                const result = await enabledApi.updateUserAppInterfaceControls(sendControls, locale);
                if (result?.resultType === 'Ok' && result.data) {
                    get().updateLocalAppInterfaceControls(result.data);
                    return result;
                }
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error updating app interface controls'];
                });
                return new UnexpectedResult<CustomControlsModel>();
            }
        };

        const updateLocalAppInterfaceControls = (appInterfaceControls: CustomControlsModel) => {
            if (!appInterfaceControls) return;
            const availableCustomControls = get().availableCustomControls;
            const deviceIndex = availableCustomControls?.findIndex((d) => d.deviceId === 'global');
            if (deviceIndex == null) return;
            updateAvailableCustomControls(deviceIndex, appInterfaceControls.controls, 'global', 'global', 'Cephable');
        };

        const updateDeviceCustomControls = async (
            deviceId: string,
            overrides: UserDeviceCustomControlOverrideModel[]
        ) => {
            const availableCustomControls = get().availableCustomControls;
            if (!availableCustomControls) return;
            const allDeviceControls = availableCustomControls.find((d) => d.deviceId === deviceId)?.controls;
            if (!allDeviceControls) return;
            const sendControls: UserDeviceCustomControlOverrideModel[] = allDeviceControls.map((c) => {
                const override = overrides.find((o) => o.deviceTypeCustomControlId === c.id);
                return { deviceTypeCustomControlId: c.id, commands: override?.commands || c?.commands || [] };
            });
            try {
                const locale = get().customControlsLocale;
                const result = await enabledApi.updateDeviceCustomControls(deviceId, sendControls, locale);
                if (result?.resultType === 'Ok' && result.data) {
                    get().updateLocalDeviceCustomControls(result.data);
                    return result;
                }
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error updating device custom controls'];
                });
                return new UnexpectedResult<CustomControlsModel>();
            }
        };

        const updateLocalDeviceCustomControls = (deviceCustomControls: CustomControlsModel) => {
            const deviceId = deviceCustomControls?.userDeviceId;
            if (!deviceId) return;
            const availableCustomControls = get().availableCustomControls;
            const deviceIndex = availableCustomControls?.findIndex((d) => d.deviceId === deviceId);
            const deviceName = useDeviceStore.getState().getDeviceName(deviceId);
            if (deviceIndex == null) return;
            updateAvailableCustomControls(deviceIndex, deviceCustomControls.controls, deviceId, deviceId, deviceName);
        };

        const updateCustomUserDeviceControls = async (
            deviceId: string,
            controlsToUpdate: CustomControlModel[],
            locale: string
        ) => {
            if (!deviceId || !controlsToUpdate) return new UnexpectedResult<CustomControlsModel>();
            if (controlsToUpdate.length === 0) return;
            try {
                await enabledApi.updateCustomUserDeviceControls(deviceId, controlsToUpdate, locale);
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error updating device custom controls'];
                });
                return new UnexpectedResult<CustomControlsModel>();
            }
        };

        const clear = () => {
            setAvailableControlsState([], []);
            set((state) => {
                state.voicePausedCustomControls = new Set();
                state.voicePrefixCustomControls = new Set();
            });
            handleVoiceDictationControlsUpdate(new Set());
            localStorage.removeItem('CUSTOM_CONTROLS');
        };

        const resetCustomControls = async (localePreferences: UpdateUserLocaleRequest) => {
            const locale = localePreferences.defaultVoiceLocale;
            set((state) => {
                state.customControlsLocale = locale;
            });
            const interfaceResult = await enabledApi.updateUserAppInterfaceControls([], locale);
            if (interfaceResult?.resultType === 'Ok' && interfaceResult.data) {
                get().updateLocalAppInterfaceControls(interfaceResult.data);
            }
            const devices = useDeviceStore.getState().devices;
            if (devices) {
                for (const device of devices) {
                    if (device.isVerified === false) continue;
                    const result = await enabledApi.updateDeviceCustomControls(device.id, [], locale);
                    if (result?.resultType === 'Ok' && result.data) {
                        set((state) => {
                            state.resetDeviceControls = true;
                        });
                        get().updateLocalDeviceCustomControls(result.data);
                    }
                }
            }
        };

        const removeDeviceControls = (deviceId: string) => {
            const state = get();
            const updatedAvailable = state.availableCustomControls?.filter((d) => d.deviceId !== deviceId) || null;
            const updatedProfile = state.profileCustomControls?.filter((d) => d.deviceId !== deviceId) || null;
            setAvailableControlsState(updatedAvailable, updatedProfile);
            resetVoiceCustomControls();
            snapshotState();
        };

        const addDeviceControls = async (device: UserDeviceModel) => {
            const locale = get().customControlsLocale;
            try {
                set((state) => {
                    state.isLoading = true;
                });
                const deviceType = device.device?.deviceType;
                if (
                    deviceType &&
                    device.customControls.length === 0 &&
                    device.customDeviceTypeControlOverrides.length === 0
                ) {
                    const dtResult = await enabledApi.getDeviceTypeCustomControls(deviceType.id, locale);
                    if (dtResult?.resultType === 'Ok' && dtResult.data) {
                        device.customControls = dtResult.data.controls.map(
                            (c) => ({ ...c, isDeviceTypeControl: true }) as CustomControlModel
                        );
                    }
                }
                if (device.customControls.length === 0 && device.customDeviceTypeControlOverrides.length === 0) {
                    return new SuccessResult<boolean>(true);
                }

                const deviceId = device.id;
                const deviceName = useDeviceStore.getState().getDeviceName(deviceId);
                const controls = getCustomControlModels(device.customControls, device.customDeviceTypeControlOverrides);

                const newEntry: AvailableCustomControlsModel = {
                    deviceId,
                    deviceTypeId: deviceType!.id,
                    name: deviceName,
                    isPrivate: deviceType?.isPrivate,
                    controls,
                };

                const state = get();
                const currentAvailable = state.availableCustomControls ?? [];
                const currentProfile = state.profileCustomControls ?? [];
                const profileControls = controls.filter((c) => c.isProfileControl);
                const newProfile =
                    profileControls.length > 0
                        ? [...currentProfile, { ...newEntry, controls: profileControls }]
                        : currentProfile;

                setAvailableControlsState([...currentAvailable, newEntry], newProfile);
                snapshotState();
                return new SuccessResult<boolean>(true);
            } catch (error) {
                console.log(error);
                set((state) => {
                    state.errors = [String(error)];
                });
                return new UnexpectedResult<boolean>();
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        return {
            // State
            availableCustomControls: null,
            currentDeviceControls: { global: undefined, device: undefined },
            customControlsLocale: DefaultVoiceLocale,
            errors: [],
            isLoading: false,
            profileCustomControls: null,
            resetDeviceControls: false,
            userLocale: DefaultVoiceLocale,
            voiceDictationCustomControls: new Set(),
            voicePausedCustomControls: new Set(),
            voicePrefixCustomControls: new Set(),

            // Actions
            setResetDeviceControls,
            setAvailableCustomControls,
            snapshotState,
            resetVoiceCustomControls,
            initializeFromStorage,
            initializeFromRemote,
            getDeviceCustomControls,
            getDeviceControlName,
            getFinalControlMacro,
            getFinalDictationControlMacro,
            getPartialControlMacro,
            getAllCustomCommandsMap,
            updateAppInterfaceControls,
            updateLocalAppInterfaceControls,
            updateDeviceCustomControls,
            updateLocalDeviceCustomControls,
            updateCustomUserDeviceControls,
            clear,
            resetCustomControls,
            removeDeviceControls,
            addDeviceControls,
        };
    })
);

// Module-level event subscriptions (replace useEffect([], []))
subscribe('device-added', (data) => {
    useGlobalCustomControlsStore.getState().addDeviceControls(data.device);
});

subscribe('remove-device', (data) => {
    useGlobalCustomControlsStore.getState().removeDeviceControls(data.device.id);
});

// Keep currentDeviceControls in sync when current device changes
useCurrentDeviceStore.subscribe((state, prevState) => {
    if (state.id !== prevState.id) {
        const available = useGlobalCustomControlsStore.getState().availableCustomControls;
        const deviceControls = computeCurrentDeviceControls(available, state.id);
        useGlobalCustomControlsStore.setState({ currentDeviceControls: deviceControls });
    }
});
